<script lang="ts">
    import {
        FALLBACK_COUNTRY_CODE,
        getDefaultCountryCode,
    } from '$lib/components/Blocks/_utils/getDefaultCountryCode';
    import { cn } from '$lib/utils/cn';

    import { AsYouType, getCountries, getPhoneCode, parsePhoneNumber } from 'libphonenumber-js';
    import uniqueId from 'lodash/uniqueId';
    import { afterUpdate } from 'svelte';
    import DeviceDetector from 'svelte-device-detector/dist';
    import clickOutside from 'svelte-outside-click';

    import DropdownInputPhoneCodeButtons from './DropdownInput/DropdownInputPhoneCodeButtons.svelte';
    import StatusIcon from './StatusIcon.svelte';
    import BottomSheet from '../../_wrappers/BottomSheet.svelte';
    import Dropdown from '../../_wrappers/Dropdown.svelte';
    import { resolveInputStyling } from '../_utils/resolveInputStyling';
    import {
        getFlagEmoji,
        parseCountryCodesInPartialNumber,
        normalisePhoneCodePrefix,
    } from '../helper';

    import type { CustomOptions } from '$lib/types/Components/FormQuestion/CustomOptions';
    import type { FormInputBinding } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';
    import type { CountryCode } from 'libphonenumber-js';

    export let placeholder = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let finalFormInput: FormInputBinding;
    export let errorMessage = '';
    export let customOptions: Partial<CustomOptions> = {};
    export let focusColor = '';
    export let outlineColor = '';
    export let theme: Theme | undefined = undefined;

    export let language: Language = 'en';

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = true;
    let inputContainerClasses: string;
    let inputClasses: string;
    let inputPlaceholderClasses: string;
    let countrySelectorContainerClasses: string;
    let selectedCountry: CountryCode = getDefaultCountryCode(
        Boolean(customOptions?.hasCountryAutoDetect),
        customOptions?.defaultCountry || FALLBACK_COUNTRY_CODE,
    );
    let countryDropdownOpen = false;
    let phoneCode = '';
    let localValue = '';
    let valueChanged = false;

    const dropdownId = uniqueId('dropdown');

    $: {
        try {
            const parsed = parsePhoneNumber(localValue, selectedCountry);
            finalFormInput.onChange(parsed.formatInternational());
        } catch {
            finalFormInput.onChange(localValue);
        }
    }
    $: {
        phoneCode = getPhoneCode(selectedCountry);

        if (!valueChanged && phoneCode) {
            localValue = `+${phoneCode}`;
        }
    }

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && valueChanged;
    });

    const closeDropdown = () => {
        countryDropdownOpen = false;
    };

    const openDropdown = () => {
        countryDropdownOpen = !countryDropdownOpen;
    };

    const handleClickOutside = () => {
        if (!countryDropdownOpen) return;

        closeDropdown();
    };

    const handleSelectCountry = (code: CountryCode) => {
        selectedCountry = code;

        if (localValue.startsWith('+')) {
            localValue = formatNumber(
                `+${getPhoneCode(code)}${localValue.slice(phoneCode.length + 1)}`,
            );
        } else if (localValue.startsWith('00')) {
            localValue = formatNumber(
                `00${getPhoneCode(code)}${localValue.slice(phoneCode.length + 2)}`,
            );
        } else {
            localValue = formatNumber(localValue);
        }

        if (!localValue) {
            valueChanged = false;
        }

        closeDropdown();
    };

    const handleBlur = (event: FocusEvent) => {
        finalFormInput.onBlur(event);
        localValue = formatNumber(localValue);
    };

    const handleInput = (event: Event) => {
        valueChanged = true;

        const target = event.target as HTMLInputElement;
        const currentValue = target.value;
        let parseResult;

        try {
            parseResult = parsePhoneNumber(currentValue);
        } catch {
            parseResult = undefined;
        }

        const parsedCountry = parseCountryCodesInPartialNumber(currentValue);

        if (parseResult?.country && parseResult?.country !== selectedCountry) {
            selectedCountry = parseResult.country;
        } else if (
            parsedCountry?.countryCodes?.[0] &&
            parsedCountry?.countryCodes?.[0] !== selectedCountry
        ) {
            selectedCountry = parsedCountry.countryCodes[0];
        }

        const { selectionStart, selectionEnd } = target;
        const isSelectionAtEnd =
            selectionStart === selectionEnd && selectionStart === currentValue.length;
        const formattedNumber = formatNumber(currentValue);

        localValue = isSelectionAtEnd ? formattedNumber : currentValue;
    };

    const formatNumber = (number: string) => {
        const asYouType = new AsYouType(selectedCountry);
        const { normalised, wasChanged } = normalisePhoneCodePrefix(number);
        const formattedResult = asYouType.input(normalised);

        if (!asYouType.getNumber()) {
            return number;
        }

        return wasChanged ? `00${formattedResult.slice(1)}` : formattedResult;
    };

    const {
        borderRadiusClass,
        shadowClass,
        borderColor,
        backgroundColor,
        textColor,
        mutedTextColor,
    } = resolveInputStyling({
        theme,
        shadowComponent: 'textInput',
    });

    $: inputContainerClasses = cn(
        borderRadiusClass,
        shadowClass,
        'fun-box-border fun-w-full fun-border fun-bg-white fun-pl-14 fun-pr-6 fun-align-middle fun-ring-2 fun-ring-transparent fun-transition-all',
        {
            ['fun-h-20']: invalid && showError,
            ['fun-h-14']: !invalid || !showError,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: active,
        },
    );

    $: inputClasses = cn(
        'fun-w-full fun-appearance-none fun-rounded-none fun-border-none fun-bg-transparent fun-align-middle fun-font-ui fun-text-base fun-font-light fun-outline-none fun-transition-all',
        !mutedTextColor && 'fun-text-black fun-placeholder-gray-500',
        {
            'fun-h-full': !invalid || !showError,
            'fun-h-14': invalid && showError,
            'fun-pr-12': invalid && showError,
            'fun-pr-10': !invalid || !showError,
        },
    );

    $: inputPlaceholderClasses = cn(
        'fun-pointer-events-none fun-absolute fun-left-24 fun-top-0 fun-flex fun-h-14 fun-w-full fun-max-w-[70%] fun-items-center fun-justify-start fun-bg-transparent fun-font-ui fun-text-base fun-font-light',
        !mutedTextColor && 'fun-text-gray-500',
        localValue.trim().length > 3 && 'fun-hidden',
        'fun-pr-14',
        {
            'fun-pr-14': invalid && showError,
            'fun-pr-6': !invalid || !showError,
        },
    );

    $: countrySelectorContainerClasses = cn('fun-absolute fun-w-full fun-transition-all', {
        'fun-top-[56px]': !invalid || !showError,
        'fun-top-[82px]': invalid && showError,
    });
</script>

<div class="fun-relative fun-box-border fun-flex fun-items-center">
    <DeviceDetector showInDevice={['mobile', 'tablet']}>
        <div use:clickOutside={handleClickOutside}>
            <BottomSheet
                open={countryDropdownOpen}
                handleClose={closeDropdown}
                title={language === 'de' ? 'Wähle ein Land' : 'Choose a country'}
                {language}
            >
                <DropdownInputPhoneCodeButtons
                    choices={getCountries()}
                    selected={selectedCountry}
                    value={selectedCountry}
                    open={countryDropdownOpen}
                    handleSelect={handleSelectCountry}
                    {language}
                />
            </BottomSheet>
        </div>
    </DeviceDetector>

    <DeviceDetector showInDevice="desktop">
        <div class={countrySelectorContainerClasses} use:clickOutside={handleClickOutside}>
            <Dropdown
                id={dropdownId}
                open={countryDropdownOpen}
                contentClass="fun-w-full fun-box-border"
            >
                <DropdownInputPhoneCodeButtons
                    choices={getCountries()}
                    selected={selectedCountry}
                    value={selectedCountry}
                    open={countryDropdownOpen}
                    handleSelect={handleSelectCountry}
                    {language}
                    {focusColor}
                    autoFocusSearchField
                />
            </Dropdown>
        </div>
    </DeviceDetector>

    <button
        on:click|preventDefault|stopPropagation={openDropdown}
        class="fun-absolute fun-left-4 fun-top-0 fun-flex fun-h-14 fun-select-none fun-items-center fun-gap-0.5"
        aria-label={language === 'de' ? 'Ländervorwahl auswählen' : 'Select country code'}
    >
        <span class="fun-text-xl">{getFlagEmoji(selectedCountry)}</span>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            class={cn('fun-transition-all', { 'fun-rotate-180': countryDropdownOpen })}
        >
            <path
                d="M4.23967 6.20041C4.5432 5.91856 5.01775 5.93613 5.2996 6.23967L8 9.14779L10.7004 6.23967C10.9823 5.93613 11.4568 5.91856 11.7603 6.20041C12.0639 6.48226 12.0815 6.95681 11.7996 7.26034L8.5496 10.7603C8.40769 10.9132 8.20855 11 8 11C7.79145 11 7.59232 10.9132 7.45041 10.7603L4.20041 7.26034C3.91856 6.95681 3.93613 6.48226 4.23967 6.20041Z"
                fill="#6B7280"
            />
        </svg>
    </button>

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div
        class={inputContainerClasses}
        style:background-color={backgroundColor}
        style:border-color={!active ? borderColor : undefined}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
    >
        <input
            class={inputClasses}
            class:themed-placeholder={!!mutedTextColor}
            style:color={textColor}
            style:--placeholder-color={mutedTextColor}
            type="tel"
            name={finalFormInput.name}
            value={localValue}
            aria-labelledby="phone-placeholder"
            on:blur={handleBlur}
            on:focus={finalFormInput.onFocus}
            on:input={handleInput}
        />

        <div class={inputPlaceholderClasses} style:color={mutedTextColor} id="phone-placeholder">
            <span class="fun-truncate">{placeholder}</span>
        </div>

        {#if errorMessage && showError}
            <div
                class="fun--mt-2.5 fun-font-ui fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600"
            >
                {errorMessage}
            </div>
        {/if}
    </div>
</div>

<style>
    input.themed-placeholder::placeholder {
        color: var(--placeholder-color);
    }
</style>
